/**
 * PendingPaymentsWidget.tsx — Botón flotante + popup de pagos manuales pendientes
 *
 * FAB fijo (esquina inferior derecha) que lista consultas con registro de pago
 * manual incompleto (N° boleta SII y/o código de transferencia) y permite
 * completarlos inline sin salir del módulo activo del portal.
 *
 * Contexto B — Portal Terapeuta (TherapistPortal.css tokens)
 *
 * Restricciones:
 * - Sin console.log con datos de pacientes (solo console.error, sin PII)
 * - Fallo de carga es silencioso — widget secundario, no bloquea el portal
 * - Reusa tal cual la RPC set_pago_manual_data (mismo patrón que Consultations.tsx)
 */

import React, { useEffect, useState } from 'react';
import './PendingPaymentsWidget.css';
import { supabase } from '../../lib/supabase/client';
import { useConfirm } from '../ui/ConfirmProvider';

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface PendingPaymentsWidgetProps {
  therapistId: string;
  isDirector: boolean;
}

interface PendingPaymentItem {
  consulta_id: string;
  paciente_nombre: string;
  paciente_apellido: string;
  fecha_hora: string;
  tipo_consulta: string;
  metodo_pago_declarado: string;
  numero_comprobante: string | null;
  numero_boleta_sii: string | null;
  /** La consulta ya tiene el cobro registrado (pago 'pagado' o estado_pago_consulta
   *  saldado) — solo falta la boleta. Viene de las RPC get_pagos_pendientes_* (SEC-023). */
  cobro_confirmado?: boolean;
  terapeuta_nombre?: string;
  terapeuta_apellido?: string;
  // FUNC-133: columnas agregadas al final de get_pagos_pendientes_* — opcionales para que el
  // frontend funcione aunque la RPC aún no las devuelva (anti deploy-gap).
  valor_consulta?: number | null;
  /** Monto con que se agendó (precio preferente transferencia/efectivo). */
  valor_final?: number | null;
  duracion?: number | null;
  modalidad?: string | null;
  tiene_tarifa_diferenciada?: boolean;
  /** Monto que el servidor cobrará si se confirma con tarjeta; null = sin re-precio o ya cobrado. */
  precio_tarjeta?: number | null;
  monto_cobrado?: number | null;
  metodo_pago_cobrado?: string | null;
}

/** Campos de registro manual de pago editables inline — mismo par que Consultations.tsx. */
type PagoManualField = 'numero_comprobante' | 'numero_boleta_sii';

type TipoClase = 'individual' | 'pareja' | 'familia' | 'sexologia';

/**
 * Medio REAL con que se cobró (FUNC-127). Dominio aceptado por la RPC
 * registrar_pago_recibido — webpay_* queda fuera (solo lo escribe el webhook
 * Transbank). Débito/crédito = POS SumUp en la clínica.
 */
type MetodoCobro = 'efectivo' | 'tarjeta_debito' | 'tarjeta_credito' | 'transferencia';

const METODO_COBRO_LABEL: Record<MetodoCobro, string> = {
  efectivo: 'Efectivo',
  tarjeta_debito: 'Débito (SumUp)',
  tarjeta_credito: 'Crédito (SumUp)',
  transferencia: 'Transferencia',
};

/** Default del selector: lo que el paciente declaró al agendar. */
function metodoCobroInicial(item: PendingPaymentItem): MetodoCobro {
  // FUNC-131: 'tarjeta' declarada = Débito/Crédito → el terapeuta elige cuál (default débito).
  if (item.metodo_pago_declarado === 'tarjeta') return 'tarjeta_debito';
  return item.metodo_pago_declarado === 'transferencia' ? 'transferencia' : 'efectivo';
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function clp(n: number): string {
  return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(n);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('es-CL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('es-CL', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Normaliza el valor crudo de tipo_consulta (que puede venir en formato legado
 * "Terapia Individual" o en formato de columna "individual") a una de las 4
 * clases CSS de tipo de terapia — mismo criterio de normalización que
 * normalizeTipo en Consultations.tsx pero orientado a las clases ppw-item__type--*.
 */
function normalizeTipoClase(tipo: string): TipoClase {
  const t = (tipo || '').toLowerCase();
  if (t.includes('sexolog')) return 'sexologia';
  if (t.includes('pareja')) return 'pareja';
  if (t.includes('famili')) return 'familia';
  return 'individual';
}

const TIPO_LABEL: Record<TipoClase, string> = {
  individual: 'Individual',
  pareja: 'Pareja',
  familia: 'Familiar',
  sexologia: 'Sexología',
};

/** Un pendiente sale de la lista cuando ya no le falta ningún campo aplicable. */
// FUNC-131: 'transferencia' declarada ahora también cubre efectivo → el código de
// transferencia solo se exige si el medio ELEGIDO para el cobro es transferencia.
function estaCompleto(item: PendingPaymentItem, metodo: MetodoCobro = metodoCobroInicial(item)): boolean {
  const boletaCompleta = !!item.numero_boleta_sii;
  const transferenciaCompleta = metodo !== 'transferencia' || !!item.numero_comprobante;
  return boletaCompleta && transferenciaCompleta;
}

// ─── Componente ───────────────────────────────────────────────────────────────

const PendingPaymentsWidget: React.FC<PendingPaymentsWidgetProps> = ({ therapistId, isDirector }) => {
  const confirm = useConfirm();
  const [pending, setPending] = useState<PendingPaymentItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  // Edición inline — un solo editor activo a la vez (mismo criterio que Consultations.tsx)
  const [editingField, setEditingField] = useState<{ consultaId: string; field: PagoManualField } | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);

  // Descarte de pendiente ("no asistió" — no hay nada que cobrar)
  const [discardingId, setDiscardingId] = useState<string | null>(null);
  const [discardError, setDiscardError] = useState<{ consultaId: string; message: string } | null>(null);

  // Confirmación de cobro (SEC-023 — registra el pago recibido, dispara el
  // split al inversor y bloquea el descarte posterior)
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  // Medio real elegido por consulta — sin entrada = default del declarado.
  const [metodoCobro, setMetodoCobro] = useState<Record<string, MetodoCobro>>({});
  const [confirmChargeError, setConfirmChargeError] = useState<{ consultaId: string; message: string } | null>(null);

  // Reversa de un cobro confirmado por error (SEC-023b — solo director:
  // borra la fila de distribución, devuelve el pago a "pendiente")
  const [revertingId, setRevertingId] = useState<string | null>(null);
  const [revertError, setRevertError] = useState<{ consultaId: string; message: string } | null>(null);

  // ── Carga inicial ────────────────────────────────────────────────────────

  useEffect(() => {
    if (!therapistId) return;
    let cancelled = false;

    const fetchPending = async () => {
      try {
        const { data, error } = isDirector
          ? await supabase.rpc('get_pagos_pendientes_director')
          : await supabase.rpc('get_pagos_pendientes_terapeuta', { p_therapist_id: therapistId });

        if (error) throw error;
        if (!cancelled) setPending((data as PendingPaymentItem[] | null) ?? []);
      } catch (err) {
        // Widget secundario: fallo de carga no bloquea el portal. Sin PII en el log.
        console.error('[PendingPaymentsWidget] Error al cargar pagos pendientes:', err);
      }
    };

    fetchPending();
    return () => { cancelled = true; };
  }, [therapistId, isDirector]);

  // ── Edición inline ───────────────────────────────────────────────────────

  const startEditing = (consultaId: string, field: PagoManualField, currentValue: string | null) => {
    setEditingField({ consultaId, field });
    setEditingValue(currentValue ?? '');
    setSaveError(null);
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditingValue('');
    setSaveError(null);
  };

  const saveField = async (consultaId: string, field: PagoManualField) => {
    const trimmed = editingValue.trim();
    setSaveError(null);

    // Una llamada por campo — el campo que no se manda no se toca en el backend.
    const { error } = field === 'numero_comprobante'
      ? await supabase.rpc('set_pago_manual_data', {
          p_consulta_id: consultaId,
          p_codigo_transferencia: trimmed,
        })
      : await supabase.rpc('set_pago_manual_data', {
          p_consulta_id: consultaId,
          p_numero_boleta: trimmed,
        });

    if (error) {
      console.error('[PendingPaymentsWidget] Error al guardar dato manual de pago:', error);
      setSaveError('No se pudo guardar. Intentá nuevamente.');
      return;
    }

    const newValue = trimmed === '' ? null : trimmed;

    // Actualización optimista local — si tras el cambio ya no le falta nada, sale de la lista.
    setPending(prev => {
      const updated = prev.map(item =>
        item.consulta_id === consultaId ? { ...item, [field]: newValue } : item
      );
      return updated.filter(item => item.consulta_id !== consultaId || !estaCompleto(item, metodoCobro[item.consulta_id]));
    });

    cancelEditing();
  };

  // ── Descartar pendiente ──────────────────────────────────────────────────
  // Confirmación vía useConfirm() compartido (mismo diálogo que el resto del
  // portal). El error de la RPC se muestra inline por ítem (discardError).

  const handleDiscard = async (item: PendingPaymentItem) => {
    const confirmed = await confirm({
      title: '¿Descartar este pendiente de pago?',
      message: `Paciente: ${item.paciente_nombre} ${item.paciente_apellido}\nFecha: ${formatDate(item.fecha_hora)} · ${formatTime(item.fecha_hora)}\n\nSe marcará la consulta como "no asistió" y saldrá de esta lista. Esta acción no se puede deshacer.`,
      confirmLabel: 'Descartar',
      tone: 'danger',
    });
    if (!confirmed) return;

    setDiscardError(null);
    setDiscardingId(item.consulta_id);
    try {
      const { error } = await supabase.rpc('descartar_consulta_pendiente_pago', {
        p_consultation_id: item.consulta_id,
      });

      if (error) throw error;

      // Actualización optimista local — mismo criterio que saveField, sin refetch completo.
      setPending(prev => prev.filter(p => p.consulta_id !== item.consulta_id));
    } catch (err) {
      // Sin PII en el log. Mensaje genérico al usuario — nunca el error crudo de Supabase
      // (puede ser la excepción de "ya tiene pago real asociado" o la RPC aún no aplicada).
      console.error('[PendingPaymentsWidget] Error al descartar pendiente de pago:', err);
      setDiscardError({
        consultaId: item.consulta_id,
        message: 'No se pudo descartar. Intentá nuevamente.',
      });
    } finally {
      setDiscardingId(null);
    }
  };

  // ── Confirmar cobro ──────────────────────────────────────────────────────
  // Registra el pago recibido vía RPC registrar_pago_recibido: crea/promueve
  // la fila en `pagos` a 'pagado' por el valor de la consulta, deja la
  // confirmación atribuida al terapeuta/director, dispara el split al
  // inversor y saca la consulta del limbo de "pendiente". A partir de acá
  // la consulta ya no se puede descartar.

  const handleConfirmCharge = async (item: PendingPaymentItem) => {
    const metodo = metodoCobro[item.consulta_id] ?? metodoCobroInicial(item);
    // FUNC-133: con tarjeta el servidor re-precia a lista cuando precio_tarjeta != null.
    const esTarjeta = metodo === 'tarjeta_debito' || metodo === 'tarjeta_credito';
    const montoCobro = esTarjeta && item.precio_tarjeta != null ? item.precio_tarjeta : item.valor_final;
    const montoTexto = montoCobro != null ? `por ${clp(montoCobro)}` : 'por el valor de la consulta';
    const confirmed = await confirm({
      title: '¿Confirmar el cobro de esta consulta?',
      message: `Paciente: ${item.paciente_nombre} ${item.paciente_apellido}\nFecha: ${formatDate(item.fecha_hora)} · ${formatTime(item.fecha_hora)}\nMétodo: ${METODO_COBRO_LABEL[metodo]}\n\nSe registra el pago ${montoTexto}, a tu nombre. Queda asentado en el sistema y no se puede deshacer.`,
      confirmLabel: 'Confirmar cobro',
      tone: 'default',
    });
    if (!confirmed) return;

    setConfirmChargeError(null);
    setConfirmingId(item.consulta_id);
    try {
      const { error } = await supabase.rpc('registrar_pago_recibido', {
        p_consulta_id: item.consulta_id,
        p_metodo_pago: metodo,
      });

      if (error) throw error;

      // Optimista: marca el cobro como confirmado. El ítem sale de la lista si
      // ya no le falta la boleta/comprobante (estaCompleto); si todavía falta,
      // se queda mostrando "Cobrado" + el campo de boleta.
      setPending(prev =>
        prev
          .map(p => (p.consulta_id === item.consulta_id ? { ...p, cobro_confirmado: true } : p))
          .filter(p => p.consulta_id !== item.consulta_id || !estaCompleto(p, metodo))
      );
    } catch (err) {
      // Sin PII en el log. Mensaje genérico — nunca el error crudo de Supabase.
      console.error('[PendingPaymentsWidget] Error al confirmar cobro:', err);
      setConfirmChargeError({
        consultaId: item.consulta_id,
        message: 'No se pudo confirmar el cobro. Intentá nuevamente.',
      });
    } finally {
      setConfirmingId(null);
    }
  };

  // ── Revertir cobro ───────────────────────────────────────────────────────
  // Solo director. Deshace una confirmación errónea: la RPC
  // revertir_pago_confirmado borra la fila distribucion_ingresos, devuelve el
  // pago a 'pendiente' y la consulta a 'sin_pago'. Rechaza si hay boleta SII
  // emitida o el split ya fue liquidado. El widget solo tiene consulta_id →
  // se resuelve el pago 'total' 'pagado' de la consulta antes de llamar.

  const handleRevertCharge = async (item: PendingPaymentItem) => {
    const confirmed = await confirm({
      title: '¿Revertir el cobro de esta consulta?',
      message: `Paciente: ${item.paciente_nombre} ${item.paciente_apellido}\nFecha: ${formatDate(item.fecha_hora)} · ${formatTime(item.fecha_hora)}\n\nSe elimina el registro del pago y su distribución al inversor. La consulta vuelve a "falta confirmar el cobro". Usalo solo si el cobro se confirmó por error.`,
      confirmLabel: 'Revertir cobro',
      tone: 'danger',
    });
    if (!confirmed) return;

    setRevertError(null);
    setRevertingId(item.consulta_id);
    try {
      const { data: pagoRow, error: lookupError } = await supabase
        .from('pagos')
        .select('id')
        .eq('consulta_id', item.consulta_id)
        .eq('tipo_pago', 'total')
        .eq('estado_pago', 'pagado')
        .maybeSingle();

      if (lookupError) throw lookupError;
      if (!pagoRow) throw new Error('pago no encontrado');

      const { error } = await supabase.rpc('revertir_pago_confirmado', {
        p_pago_id: pagoRow.id,
      });

      if (error) throw error;

      // Optimista: el ítem vuelve a "falta confirmar el cobro".
      setPending(prev =>
        prev.map(p => (p.consulta_id === item.consulta_id ? { ...p, cobro_confirmado: false } : p))
      );
    } catch (err) {
      // Sin PII en el log. Mensaje genérico — nunca el error crudo de Supabase.
      console.error('[PendingPaymentsWidget] Error al revertir cobro:', err);
      setRevertError({
        consultaId: item.consulta_id,
        message: 'No se pudo revertir el cobro. Intentá nuevamente.',
      });
    } finally {
      setRevertingId(null);
    }
  };

  // ── Render de un mini-campo editable ─────────────────────────────────────

  const renderField = (item: PendingPaymentItem, field: PagoManualField, label: string) => {
    const isEditing = editingField?.consultaId === item.consulta_id && editingField.field === field;
    const value = item[field];

    if (isEditing) {
      return (
        <div className="ppw-field">
          <span className="ppw-field__label">{label}</span>
          <div className="ppw-field__edit">
            <input
              className="ppw-field__input"
              type="text"
              value={editingValue}
              onChange={e => { setEditingValue(e.target.value); setSaveError(null); }}
              onBlur={() => saveField(item.consulta_id, field)}
              onKeyDown={e => {
                if (e.key === 'Enter') saveField(item.consulta_id, field);
                if (e.key === 'Escape') cancelEditing();
              }}
              autoFocus
            />
            {saveError && <span className="ppw-field__error">{saveError}</span>}
          </div>
        </div>
      );
    }

    return (
      <div className="ppw-field">
        <span className="ppw-field__label">{label}</span>
        <span
          className="ppw-field__value"
          title="Clic para editar"
          onClick={() => startEditing(item.consulta_id, field, value)}
        >
          {value ?? <span className="ppw-field__value--empty">— agregar</span>}
        </span>
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <button
        type="button"
        className={`ppw-fab${isOpen ? ' ppw-fab--open' : ''}`}
        onClick={() => setIsOpen(prev => !prev)}
        aria-label={isOpen ? 'Cerrar pagos pendientes' : 'Ver pagos pendientes'}
      >
        <span className="ppw-fab__icon">+</span>
        {pending.length > 0 && (
          <span className="ppw-fab__badge">{pending.length}</span>
        )}
      </button>

      {isOpen && (
        <div className="ppw-overlay" onClick={() => setIsOpen(false)}>
          <div className="ppw-modal" onClick={e => e.stopPropagation()}>
            <div className="ppw-modal__header">
              <h3 className="ppw-modal__title">Pagos pendientes</h3>
              {pending.length > 0 && (
                <span className="ppw-modal__count">{pending.length}</span>
              )}
              <button
                type="button"
                className="ppw-modal__close"
                onClick={() => setIsOpen(false)}
                aria-label="Cerrar"
              >
                <i className="fas fa-times" />
              </button>
            </div>

            <div className="ppw-modal__body">
              {pending.length === 0 ? (
                <div className="ppw-empty">
                  <i className="fas fa-check-circle ppw-empty__icon" />
                  <span className="ppw-empty__text">Todo al día</span>
                  <span className="ppw-empty__subtext">No hay pagos manuales pendientes de completar</span>
                </div>
              ) : (
                pending.map(item => {
                  const tipoClase = normalizeTipoClase(item.tipo_consulta);
                  const showTransferencia = (metodoCobro[item.consulta_id] ?? metodoCobroInicial(item)) === 'transferencia';

                  const isDiscarding = discardingId === item.consulta_id;
                  const isConfirming = confirmingId === item.consulta_id;
                  const isReverting = revertingId === item.consulta_id;
                  const isBusy = isDiscarding || isConfirming || isReverting;

                  return (
                    <div key={item.consulta_id} className="ppw-item">
                      <div className="ppw-item__row-top">
                        <div className="ppw-item__info">
                          <span className="ppw-item__patient">
                            {item.paciente_nombre} {item.paciente_apellido}
                          </span>
                          <span className="ppw-item__meta">
                            {formatDate(item.fecha_hora)} · {formatTime(item.fecha_hora)}
                            <span className={`ppw-item__type ppw-item__type--${tipoClase}`}>
                              {TIPO_LABEL[tipoClase]}
                            </span>
                            {item.valor_final != null && (
                              <span className="ppw-item__amount">{clp(item.valor_final)}</span>
                            )}
                          </span>
                        </div>
                        {/* Descartar ("no asistió") no aplica si el cobro ya está
                            registrado — la RPC lo rechazaría igual. */}
                        {!item.cobro_confirmado && (
                          <button
                            type="button"
                            className="ppw-item__discard"
                            onClick={() => handleDiscard(item)}
                            disabled={isBusy}
                            aria-label="Descartar pendiente de pago — el paciente no asistió"
                            title="Descartar — el paciente no asistió"
                          >
                            <i className={`fas ${isDiscarding ? 'fa-spinner fa-spin' : 'fa-times'}`} aria-hidden="true" />
                          </button>
                        )}
                      </div>
                      {isDirector && item.terapeuta_nombre && (
                        <span className="ppw-item__meta">
                          {item.terapeuta_nombre} {item.terapeuta_apellido}
                        </span>
                      )}
                      <div className="ppw-item__fields">
                        {showTransferencia && renderField(item, 'numero_comprobante', 'Código transferencia')}
                        {renderField(item, 'numero_boleta_sii', 'N° Boleta SII')}
                      </div>
                      <div className="ppw-item__actions">
                        {item.cobro_confirmado ? (
                          <>
                            <span className="ppw-item__paid" title="Esta consulta ya tiene el pago registrado — solo falta cargar la boleta">
                              <i className="fas fa-circle-check" aria-hidden="true" />
                              Pago registrado
                            </span>
                            {isDirector && (
                              <button
                                type="button"
                                className="ppw-item__revert"
                                onClick={() => handleRevertCharge(item)}
                                disabled={isBusy}
                                title="Revertir el cobro registrado — solo si se confirmó por error"
                              >
                                <i className={`fas ${isReverting ? 'fa-spinner fa-spin' : 'fa-rotate-left'}`} aria-hidden="true" />
                                Revertir
                              </button>
                            )}
                          </>
                        ) : (
                          <>
                          <select
                            className="ppw-item__method"
                            value={metodoCobro[item.consulta_id] ?? metodoCobroInicial(item)}
                            onChange={e =>
                              setMetodoCobro(prev => ({ ...prev, [item.consulta_id]: e.target.value as MetodoCobro }))
                            }
                            disabled={isBusy}
                            aria-label="Medio con que se cobró"
                          >
                            {(Object.keys(METODO_COBRO_LABEL) as MetodoCobro[]).map(m => (
                              <option key={m} value={m}>{METODO_COBRO_LABEL[m]}</option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="ppw-item__confirm"
                            onClick={() => handleConfirmCharge(item)}
                            disabled={isBusy}
                            title="Registrar que se cobró esta consulta"
                          >
                            <i className={`fas ${isConfirming ? 'fa-spinner fa-spin' : 'fa-check'}`} aria-hidden="true" />
                            Confirmar cobro
                          </button>
                          </>
                        )}
                      </div>
                      {/* FUNC-133: aviso de re-precio con tarjeta. El servidor cobra precio_tarjeta si se
                          confirma con débito/crédito. Fallback genérico (FUNC-131) solo mientras la RPC
                          no devuelve las columnas nuevas (valor_final === undefined). */}
                      {!item.cobro_confirmado && (() => {
                        const m = metodoCobro[item.consulta_id] ?? metodoCobroInicial(item);
                        if (m !== 'tarjeta_debito' && m !== 'tarjeta_credito') return null;
                        if (item.valor_final === undefined) {
                          return item.metodo_pago_declarado !== 'tarjeta' ? (
                            <p className="ppw-item__price-warning" role="status">
                              <i className="fas fa-triangle-exclamation" aria-hidden="true" />
                              Se agendó con precio preferente de transferencia/efectivo. En sesiones presenciales,
                              con tarjeta rige el valor de lista — ajusta el precio antes de cobrar si corresponde.
                            </p>
                          ) : null;
                        }
                        if (item.precio_tarjeta == null) return null;
                        return (
                          <p className="ppw-item__price-warning" role="status">
                            <i className="fas fa-triangle-exclamation" aria-hidden="true" />
                            Con débito/crédito se cobrará {clp(item.precio_tarjeta)}
                            {item.valor_final != null && <> (agendada a {clp(item.valor_final)} con transferencia/efectivo)</>}
                          </p>
                        );
                      })()}
                      {discardError?.consultaId === item.consulta_id && (
                        <span className="ppw-item__discard-error">{discardError.message}</span>
                      )}
                      {confirmChargeError?.consultaId === item.consulta_id && (
                        <span className="ppw-item__discard-error">{confirmChargeError.message}</span>
                      )}
                      {revertError?.consultaId === item.consulta_id && (
                        <span className="ppw-item__discard-error">{revertError.message}</span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PendingPaymentsWidget;
